import {
  AdminHeader,
  AppShell,
  Box,
  getModuleUrl,
  i18n,
  LanguageSwitcher,
  LoadingOverlay,
  Sidebar,
  Text,
  Title,
  useTenant,
  useTheme,
  useTokenVars,
  type SidebarItem,
  type ProfileMenuSection,
} from "@dehub/theme";
import {
  BankIcon,
  HouseIcon,
  IdentificationCardIcon,
  KeyIcon,
  LockKeyIcon,
  StorefrontIcon,
  UserCircleIcon,
  UserIcon,
  UsersIcon,
  WarehouseIcon,
} from "@phosphor-icons/react";
import React, { JSX, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import useSWR from "swr";
import { LogoutApi, NotificationApi } from "@/apis";
import type { IAuth } from "@/models/Auth";
import { authChange } from "@/store/Auth";
import { message } from "@/utils/message";
import { renderName } from "@/utils/render-name";
import classes from "./layout.module.css";
import { AppsMenuWithBadge } from "./AppsMenuWithBadge";
import { Breadcrumb } from "./breadcrumb";

const navKeys: { titleKey: string; icon: SidebarItem["icon"]; link: string }[] = [
  { titleKey: "nav.orgInfo", icon: IdentificationCardIcon, link: "/organization" },
  { titleKey: "nav.business", icon: StorefrontIcon, link: "/business" },
  { titleKey: "nav.branches", icon: HouseIcon, link: "/our-sectors" },
  { titleKey: "nav.warehouses", icon: WarehouseIcon, link: "/our-warehouse" },
];

export function Layout() {
  useTokenVars();
  const [opened, setOpened] = useState<boolean>(false);
  const { tokens } = useTheme();
  const tenant = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation("layout");
  const { user } = useSelector((state: { auth: IAuth }) => state.auth);

  const userName = renderName(user) || "";
  const userEmail = (user as any)?.email || "";
  const userInitial = (userName.trim().charAt(0) || "U").toUpperCase();
  const businessName = user?.partner?.businessName || "";

  const items: SidebarItem[] = navKeys.map((n) => ({
    key: n.link,
    label: t(n.titleKey),
    link: n.link,
    icon: n.icon,
  }));
  // The old supplier / buyer URLs redirect to /business; the menu item is lit on all three.
  const activePath = /^\/our-(supplier|buyer)(\/|$)/.test(location.pathname) ? "/business" : location.pathname;
  const activeKey = items.find((it) => activePath === it.link || activePath.startsWith(it.link + "/"))?.key;
  const pageTitle = t("partnerManagement");

  const onLogout = async () => {
    try {
      await LogoutApi.logout();
    } catch (error) {
      const err = error as { message?: string };
      message.error(err?.message ?? "");
    } finally {
      dispatch(authChange(undefined));
      window.location.href = `${getModuleUrl("", tenant?.modulesDomain)}/login?logout=1`;
    }
  };

  const onProfile = () => {
    navigate("/organization");
  };

  const profileSections: ProfileMenuSection[] = useMemo(() => {
    const authRoot = getModuleUrl("", tenant?.modulesDomain);
    return [
      {
        items: [
          { icon: UserCircleIcon, label: t("myInfo"), href: `${authRoot}/auth/profile/dan` },
          { icon: BankIcon, label: t("partnerManagement"), onClick: () => navigate("/organization") },
        ],
      },
      {
        items: [
          { icon: KeyIcon, label: t("profileMenu.changePin"), href: `${authRoot}/auth/profile/dan?profileAction=pin` },
          { icon: LockKeyIcon, label: t("profileMenu.changePassword"), href: `${authRoot}/auth/profile/dan?profileAction=password` },
        ],
      },
    ];
  }, [tenant?.modulesDomain, navigate, t]);

  const { data: notifData, mutate: refetchNotifs } = useSWR<any>(
    "/2fa/auth/fcm/list",
    () => NotificationApi.list({ offset: { page: 1, limit: 20 } }),
    { fallbackData: { rows: [], count: 0 }, revalidateOnFocus: false },
  );
  const notifications = (notifData?.rows ?? []).map((n: any) => mapNotif(n));

  const onMarkAllNotifsRead = async () => {
    try {
      await Promise.all(
        (notifData?.rows ?? [])
          .filter((n: any) => !n.seen)
          .map((n: any) => NotificationApi.seen(n.id)),
      );
      refetchNotifs();
    } catch {
      // silent
    }
  };

  const onNotificationClick = async (n: { id: string | number; unread?: boolean }) => {
    try {
      if (n.unread) {
        await NotificationApi.seen(`${n.id}`);
        refetchNotifs();
      }
    } catch {
      // silent
    }
  };

  const m = (mod: string) => getModuleUrl(mod, tenant?.modulesDomain);
  const modules = [
    { id: "partner", label: t("partnerModule", { defaultValue: "Партнер" }), icon: UsersIcon, href: m("partner"), active: true },
    { id: "user", label: t("userModule", { defaultValue: "Хэрэглэгч" }), icon: UserIcon, href: m("user") },
  ];

  return (
    <AppShell
      padding={0}
      header={{ height: 60 }}
      // DeHUB UI standard rail: 104px (the theme default).
      navbar={{ width: 104, breakpoint: "sm", collapsed: { mobile: !opened } }}
      styles={{
        header: {
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 200,
        },
        // White work area (canvas): the tenant grey left a strip under the white page panel.
        main: { backgroundColor: "var(--mantine-color-white)" },
      }}
    >
      <AppShell.Header>
        <AdminHeader
          tenant={{
            name: businessName,
            logo: tenant?.logo || null,
            domain: (tenant as any)?.domain || "",
          }}
          pageTitle={pageTitle}
          headerGradient={tokens.header.gradient}
          headerTextColor={tokens.header.textColor}
          userName={userName}
          userEmail={userEmail}
          userInitial={userInitial}
          userAvatar={(user as any)?.avatar || undefined}
          onProfile={onProfile}
          onLogout={onLogout}
          onSwitchTenant={() => {
            window.location.href = `${getModuleUrl("", tenant?.modulesDomain)}/businesses`;
          }}
          profileSections={profileSections}
          logoHref={`${m("cp")}?home=business`}
          onLogoClick={(e) => {
            e.preventDefault();
            window.location.href = `${m("cp")}?home=business`;
          }}
          preNotificationsSlot={<AppsMenuWithBadge modules={modules} align="right" />}
          localeSlot={
            <Box style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <LanguageSwitcher />
            </Box>
          }
          notifications={notifications}
          onMarkAllNotifsRead={onMarkAllNotifsRead}
          onNotificationClick={onNotificationClick}
        />
      </AppShell.Header>
      <AppShell.Navbar p={0} style={{ background: "transparent", border: "none" }}>
        <Sidebar
          items={items}
          activeKey={activeKey}
          ariaLabel={t("nav.label")}
          onPick={(_key, link) => {
            navigate(link);
            setOpened(false);
          }}
        />
      </AppShell.Navbar>
      <AppShell.Main className={classes.main}>
        <Box className={classes.mainInner}>
          <Outlet />
        </Box>
      </AppShell.Main>
    </AppShell>
  );
}

type PageProps = {
  title: string | JSX.Element;
  subTitle?: string | JSX.Element;
  extra?: React.ReactNode[];
  children: React.ReactNode;
  loading?: boolean;
  breadcrumb?: { to?: string; label: string }[];
  bgColor?: string;
};

export function PageLayout({
  title,
  subTitle,
  breadcrumb,
  extra = [],
  children,
  loading = false,
}: Readonly<PageProps>) {
  return (
    <Box className={classes.page} pos="relative" aria-busy={loading || undefined}>
      <LoadingOverlay visible={loading} />
      {breadcrumb && breadcrumb.length > 0 ? <Breadcrumb data={breadcrumb} /> : null}
      {title || subTitle || extra.length > 0 ? (
        <Box className={classes.head}>
          <Box className={classes.headText}>
            {title ? (
              <Title order={1} className={classes.heading}>
                {title}
              </Title>
            ) : null}
            {subTitle ? (
              <Text component="p" className={classes.sub}>
                {subTitle}
              </Text>
            ) : null}
          </Box>
          {extra.length > 0 ? (
            <Box className={classes.actions}>
              {extra.map((it, i) => (
                <React.Fragment key={i}>{it}</React.Fragment>
              ))}
            </Box>
          ) : null}
        </Box>
      ) : null}
      {children}
    </Box>
  );
}

type SectionProps = {
  left?: JSX.Element[];
  right?: JSX.Element[];
};

export function PageSection({ left, right }: Readonly<SectionProps>) {
  return (
    <Box className={classes.pageSection}>
      <Box className={classes.sectionElement}>{left}</Box>
      <Box className={classes.sectionElement}>{right}</Box>
    </Box>
  );
}

function mapNotif(n: any) {
  const created = n.createdAt ? new Date(n.createdAt) : new Date();
  const ageDays = (Date.now() - created.getTime()) / (1000 * 60 * 60 * 24);
  const group: "today" | "week" = ageDays < 1 ? "today" : "week";
  return {
    id: n.id,
    kind: detectKind(n.type ?? n.category ?? n.body),
    title: n.title ?? n.subject ?? i18n.t("layout:notif.default"),
    body: n.body ?? n.message ?? "",
    time: relativeTime(created),
    ts: created.toISOString().slice(0, 16).replace("T", " "),
    group,
    unread: !n.seen,
    action: n.actionLabel,
  };
}

function detectKind(value?: string) {
  const v = String(value || "").toLowerCase();
  if (v.includes("order")) return "order" as const;
  if (v.includes("payment") || v.includes("төлбөр")) return "payment" as const;
  if (v.includes("contract") || v.includes("гэрээ")) return "contract" as const;
  if (v.includes("partner") || v.includes("партнер")) return "partner" as const;
  return "alert" as const;
}

function relativeTime(d: Date): string {
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return i18n.t("layout:time.justNow");
  const min = Math.floor(sec / 60);
  if (min < 60) return i18n.t("layout:time.minAgo", { count: min });
  const hr = Math.floor(min / 60);
  if (hr < 24) return i18n.t("layout:time.hourAgo", { count: hr });
  const day = Math.floor(hr / 24);
  if (day === 1) return i18n.t("layout:time.yesterday");
  if (day < 7) return i18n.t("layout:time.dayAgo", { count: day });
  return d.toLocaleDateString();
}
